import React, { useState, useEffect } from 'react';
import styles from './Canvas.module.css';
import historyManager from '../../services/historyManager';

const CanvasFrame = ({ canvas, isActive, onEvent, onFocusChange }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentCode, setCurrentCode] = useState(canvas?.code);
  const [isFocused, setIsFocused] = useState(false);
  const iframeRef = React.useRef(null);

  // Only update code if it actually changes
  useEffect(() => {
    if (canvas?.code !== currentCode) {
      console.log('Canvas code changed:', canvas?.id);
      setCurrentCode(canvas.code);
      // Only reset loaded state if code actually changed
      setIsLoaded(false);
    }
  }, [canvas?.code]);

  // Reset focus when canvas becomes inactive
  useEffect(() => {
    if (!isActive) {
      setIsFocused(false);
    }
  }, [isActive]);

  // Notify parent component when focus changes
  useEffect(() => {
    if (onFocusChange && isActive) {
      console.log('Notifying parent of focus change:', isFocused);
      onFocusChange(isFocused);
    }
  }, [isFocused, onFocusChange, isActive]);

  // Handle document clicks to reset focus
  useEffect(() => {
    const handleDocumentClick = (e) => {
      const iframe = iframeRef.current;
      if (!iframe) return;

      // Check if click is outside this iframe
      const rect = iframe.getBoundingClientRect();
      const isOutside = 
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom;

      if (isOutside) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  // Process code content to add message handlers
  const processCodeContent = (code) => {
    const messageHandlerScript = `
      <script>
        // Add global error handler for iframe
        window.onerror = function(msg, url, line, col, error) {
          window.parent.postMessage({
            type: 'error_detected',
            error: msg,
            location: {
              url: url,
              line: line,
              column: col
            },
            stack: error?.stack,
            context: 'Canvas global error handler'
          }, '*');
          return false; // Let the error propagate
        };

        window.addEventListener('message', function(event) {
          if (event.data && event.data.type === 'execute') {
            try {
              const result = new Function(event.data.code)();
            } catch (error) {
              console.error('Error executing code:', error);
              window.parent.postMessage({
                type: 'error_detected',
                error: error.message,
                stack: error.stack,
                context: 'Code execution'
              }, '*');
            }
          } else if (event.data && event.data.type === 'setup_listeners') {
            try {
              const result = new Function(event.data.code)();
            } catch (error) {
              console.error('Error setting up listeners:', error);
              window.parent.postMessage({
                type: 'error_detected',
                error: error.message,
                stack: error.stack,
                context: 'Listener setup'
              }, '*');
            }
          }
        });

        window.initializeEventHandlers = function() {
          console.log('Event handlers initialized');
        };
      </script>
    `;

    return code.replace('</head>', messageHandlerScript + '</head>');
  };

  // Setup iframe event listeners
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !isActive) return;

    const setupIframeContent = () => {
      try {
        // Setup click handler in iframe
        iframe.contentWindow.postMessage({
          type: 'setup_listeners',
          code: `
            // Remove any existing click listeners first
            if (window._iframeClickHandler) {
              document.removeEventListener('mousedown', window._iframeClickHandler);
            }
            
            // Create new click handler
            window._iframeClickHandler = (e) => {
              window.parent.postMessage({ type: 'iframe_clicked' }, '*');
            };
            
            // Add capture phase listener to ensure we get the event first
            document.addEventListener('mousedown', window._iframeClickHandler, true);
          `
        }, '*');
      } catch (error) {
        console.error('Error setting up iframe listeners:', error);
      }
    };

    // Handle messages from iframe
    const handleMessage = (event) => {
      if (event.source !== iframe.contentWindow) return;
      
      const data = event.data;
      console.log('Received message from iframe:', data);

      // Handle different message types
      switch (data?.type) {
        case 'iframe_clicked':
          setIsFocused(true);
          break;

        case 'state':
          // Track state events in history
          historyManager.addEvent(`Canvas ${canvas.id} state update: ${JSON.stringify(data)}`);
          break;

        case 'immediate':
          // The event is already being passed up to Canvas.jsx
          // where onImmediateEvent is called. No change needed here.
          // Just log it and let it bubble up.
          historyManager.addEvent(`Canvas ${canvas.id} immediate event: ${JSON.stringify(data)}`);
          onEvent?.(data);
          break;

        default:
          // Forward any other messages to parent with complete data
          if (data?.type) {
            historyManager.addEvent(`Canvas ${canvas.id} event ${data.type}: ${JSON.stringify(data)}`);
            onEvent?.(data);
          }
      }
    };

    // Setup listeners
    window.addEventListener('message', handleMessage);
    iframe.addEventListener('load', () => {
      console.log('Iframe loaded, setting up content...');
      setupIframeContent();
      setIsFocused(true); // Set focus when iframe loads initially
    });

    // Handle direct clicks on the iframe
    iframe.addEventListener('mousedown', (e) => {
      console.log('Direct iframe click');
      setIsFocused(true);
      e.stopPropagation();
    });

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
      try {
        if (iframe.contentWindow) {
          iframe.contentWindow.postMessage({
            type: 'setup_listeners',
            code: `
              if (window._iframeClickHandler) {
                document.removeEventListener('mousedown', window._iframeClickHandler);
                delete window._iframeClickHandler;
              }
            `
          }, '*');
        }
      } catch (error) {
        // Ignore cross-origin errors
      }
    };
  }, [isActive]);

  // Debug logging
  useEffect(() => {
    console.log('CanvasFrame:', { 
      canvasId: canvas?.id,
      isActive,
      isLoaded,
      isFocused,
      codeLength: currentCode?.length
    });
  }, [canvas?.id, isActive, isLoaded, isFocused, currentCode]);

  if (!canvas?.code) {
    console.log('No code for canvas:', canvas?.id);
    return null;
  }

  return (
    <iframe
      ref={iframeRef}
      srcDoc={processCodeContent(currentCode)}
      title={canvas.title || 'Canvas Content'}
      className={`${styles.canvasFrame} ${isFocused ? styles.focused : ''}`}
      sandbox="allow-scripts allow-modals allow-forms allow-popups"
      allow="clipboard-read; clipboard-write"
      data-canvas-id={`id-${canvas.id}`}
      style={{
        display: isActive ? 'block' : 'none',
        opacity: isLoaded ? 1 : 0,
        transition: 'all 0.2s ease',
        transform: `scale(${isActive ? 1 : 0.95})`,
        pointerEvents: isActive ? 'auto' : 'none',
        borderRadius: '4px'
      }}
      onMouseDown={(e) => {
        console.log('Iframe mousedown');
        setIsFocused(true);
        e.stopPropagation();
      }}
      onLoad={() => {
        console.log('Iframe loaded:', canvas.id);
        setIsLoaded(true);
      }}
      onError={(e) => {
        console.error('Iframe error:', e);
        setIsLoaded(false);
      }}
    />
  );
};

export default CanvasFrame;